import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { trackFeature, FEATURE_KEYS } from "../../services/telemetry";
import {
  getForms,
  getForm,
  createForm,
  updateForm,
  deleteForm,
  duplicateForm,
  getTemplates,
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
  duplicateTemplate,
  // Digital Signature Functions
  sendFormForSignature,
  getPendingSignatureLinks,
  deleteSignatureLink,
  cancelSignatureLink,
  getSignedFormsForPatient,
  getSignedForm,
} from "../../firebase";

export const formsApi = createApi({
  reducerPath: "formsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Form", "Template", "SignatureLink", "SignedForm"],
  endpoints: (builder) => ({
    // ============================================
    // FORMS
    // ============================================

    // Get all forms
    getForms: builder.query({
      async queryFn({ clinicId }) {
        try {
          const forms = await getForms(clinicId);
          return { data: forms };
        } catch (error) {
          console.error("[CK-7060] Error fetching forms:", {
            errorCode: "CK-7060",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Form", id })),
              { type: "Form", id: "LIST" },
            ]
          : [{ type: "Form", id: "LIST" }],
    }),

    // Get single form
    getForm: builder.query({
      async queryFn({ clinicId, formId }) {
        try {
          const form = await getForm(clinicId, formId);
          return { data: form };
        } catch (error) {
          console.error("[CK-7061] Error fetching form:", {
            errorCode: "CK-7061",
            errorMessage: error.message,
            clinicId,
            formId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { formId }) => [{ type: "Form", id: formId }],
    }),

    // Create form
    createForm: builder.mutation({
      async queryFn({ clinicId, formData, creatorId, creatorName }) {
        try {
          const form = await createForm(clinicId, formData, creatorId, creatorName);
          return { data: form };
        } catch (error) {
          console.error("[CK-7062] Error creating form:", {
            errorCode: "CK-7062",
            errorMessage: error.message,
            clinicId,
            formData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Form", id: "LIST" }],
    }),

    // Update form
    updateForm: builder.mutation({
      async queryFn({ clinicId, formId, formData, editorId, editorName }) {
        try {
          const form = await updateForm(clinicId, formId, formData, editorId, editorName);
          return { data: form };
        } catch (error) {
          console.error("[CK-7063] Error updating form:", {
            errorCode: "CK-7063",
            errorMessage: error.message,
            clinicId,
            formId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { formId }) => [
        { type: "Form", id: formId },
        { type: "Form", id: "LIST" },
      ],
    }),

    // Delete form
    deleteForm: builder.mutation({
      async queryFn({ clinicId, formId, formName, therapistId, therapistName }) {
        try {
          await deleteForm(clinicId, formId, formName, therapistId, therapistName);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7064] Error deleting form:", {
            errorCode: "CK-7064",
            errorMessage: error.message,
            clinicId,
            formId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Form", id: "LIST" }],
    }),

    // Duplicate form
    duplicateForm: builder.mutation({
      async queryFn({ clinicId, formId, creatorId, creatorName }) {
        try {
          const form = await duplicateForm(clinicId, formId, creatorId, creatorName);
          return { data: form };
        } catch (error) {
          console.error("[CK-7065] Error duplicating form:", {
            errorCode: "CK-7065",
            errorMessage: error.message,
            clinicId,
            formId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Form", id: "LIST" }],
    }),

    // ============================================
    // TEMPLATES
    // ============================================

    // Get all templates
    getTemplates: builder.query({
      async queryFn({ clinicId }) {
        try {
          const templates = await getTemplates(clinicId);
          return { data: templates };
        } catch (error) {
          console.error("[CK-7070] Error fetching templates:", {
            errorCode: "CK-7070",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Template", id })),
              { type: "Template", id: "LIST" },
            ]
          : [{ type: "Template", id: "LIST" }],
    }),

    // Get single template
    getTemplate: builder.query({
      async queryFn({ clinicId, templateId }) {
        try {
          const template = await getTemplate(clinicId, templateId);
          return { data: template };
        } catch (error) {
          console.error("[CK-7071] Error fetching template:", {
            errorCode: "CK-7071",
            errorMessage: error.message,
            clinicId,
            templateId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { templateId }) => [{ type: "Template", id: templateId }],
    }),

    // Create template
    createTemplate: builder.mutation({
      async queryFn({ clinicId, templateData, creatorId, creatorName }) {
        try {
          const template = await createTemplate(clinicId, templateData, creatorId, creatorName);
          return { data: template };
        } catch (error) {
          console.error("[CK-7072] Error creating template:", {
            errorCode: "CK-7072",
            errorMessage: error.message,
            clinicId,
            templateData,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Template", id: "LIST" }],
    }),

    // Update template
    updateTemplate: builder.mutation({
      async queryFn({ clinicId, templateId, templateData, editorId, editorName }) {
        try {
          const template = await updateTemplate(clinicId, templateId, templateData, editorId, editorName);
          return { data: template };
        } catch (error) {
          console.error("[CK-7073] Error updating template:", {
            errorCode: "CK-7073",
            errorMessage: error.message,
            clinicId,
            templateId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { templateId }) => [
        { type: "Template", id: templateId },
        { type: "Template", id: "LIST" },
      ],
    }),

    // Delete template
    deleteTemplate: builder.mutation({
      async queryFn({ clinicId, templateId, templateName, therapistId, therapistName }) {
        try {
          await deleteTemplate(clinicId, templateId, templateName, therapistId, therapistName);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7074] Error deleting template:", {
            errorCode: "CK-7074",
            errorMessage: error.message,
            clinicId,
            templateId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Template", id: "LIST" }],
    }),

    // Duplicate template
    duplicateTemplate: builder.mutation({
      async queryFn({ clinicId, templateId, creatorId, creatorName }) {
        try {
          const template = await duplicateTemplate(clinicId, templateId, creatorId, creatorName);
          return { data: template };
        } catch (error) {
          console.error("[CK-7075] Error duplicating template:", {
            errorCode: "CK-7075",
            errorMessage: error.message,
            clinicId,
            templateId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Template", id: "LIST" }],
    }),

    // ============================================
    // DIGITAL SIGNATURE ENDPOINTS
    // ============================================

    // Send form for signature
    sendFormForSignature: builder.mutation({
      async queryFn({ clinicId, formId, patientId, messageTemplate, systemForm }) {
        try {
          const result = await sendFormForSignature(clinicId, formId, patientId, messageTemplate, systemForm);
          trackFeature(FEATURE_KEYS.DIGITAL_SIGNATURE);
          return { data: result };
        } catch (error) {
          console.error("[CK-7080] Error sending form for signature:", {
            errorCode: "CK-7080",
            errorMessage: error.message,
            clinicId,
            formId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "SignatureLink", id: "LIST" }],
    }),

    // Get pending signature links
    getPendingSignatureLinks: builder.query({
      async queryFn({ clinicId, therapistId }) {
        try {
          const links = await getPendingSignatureLinks(clinicId, therapistId);
          return { data: links };
        } catch (error) {
          console.error("[CK-7081] Error fetching pending signature links:", error.message);
          console.error("[CK-7081] Details:", { clinicId, therapistId, timestamp: new Date().toISOString() });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "SignatureLink", id })),
              { type: "SignatureLink", id: "LIST" },
            ]
          : [{ type: "SignatureLink", id: "LIST" }],
    }),

    // Cancel signature link
    cancelSignatureLink: builder.mutation({
      async queryFn({ clinicId, linkId }) {
        try {
          const result = await cancelSignatureLink(clinicId, linkId);
          return { data: result };
        } catch (error) {
          console.error("[CK-7082] Error cancelling signature link:", {
            errorCode: "CK-7082",
            errorMessage: error.message,
            clinicId,
            linkId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { linkId }) => [
        { type: "SignatureLink", id: linkId },
        { type: "SignatureLink", id: "LIST" },
      ],
    }),

    // Delete signature link (only pending)
    deleteSignatureLink: builder.mutation({
      async queryFn({ clinicId, linkId, therapistId, therapistName }) {
        try {
          await deleteSignatureLink(clinicId, linkId, therapistId, therapistName);
          return { data: { success: true } };
        } catch (error) {
          console.error("[CK-7083] Error deleting signature link:", {
            errorCode: "CK-7083",
            errorMessage: error.message,
            clinicId,
            linkId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "SignatureLink", id: "LIST" }],
    }),

    // Get signed forms for patient
    getSignedFormsForPatient: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const forms = await getSignedFormsForPatient(clinicId, patientId);
          return { data: forms };
        } catch (error) {
          console.error("[CK-7084] Error fetching signed forms for patient:", {
            errorCode: "CK-7084",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "SignedForm", id })),
              { type: "SignedForm", id: `PATIENT_${patientId}` },
            ]
          : [{ type: "SignedForm", id: `PATIENT_${patientId}` }],
    }),

    // Get single signed form
    getSignedForm: builder.query({
      async queryFn({ clinicId, signedFormId }) {
        try {
          const form = await getSignedForm(clinicId, signedFormId);
          return { data: form };
        } catch (error) {
          console.error("[CK-7085] Error fetching signed form:", {
            errorCode: "CK-7085",
            errorMessage: error.message,
            clinicId,
            signedFormId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { signedFormId }) => [
        { type: "SignedForm", id: signedFormId },
      ],
    }),
  }),
});

export const {
  // Forms
  useGetFormsQuery,
  useGetFormQuery,
  useCreateFormMutation,
  useUpdateFormMutation,
  useDeleteFormMutation,
  useDuplicateFormMutation,
  // Templates
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useDeleteTemplateMutation,
  useDuplicateTemplateMutation,
  // Digital Signatures
  useSendFormForSignatureMutation,
  useGetPendingSignatureLinksQuery,
  useCancelSignatureLinkMutation,
  useDeleteSignatureLinkMutation,
  useGetSignedFormsForPatientQuery,
  useGetSignedFormQuery,
} = formsApi;

